/**
 * MeterReadingsManagement — Phase 03 redesign
 * Dense table + right drawer with verify/reject, react-query
 */
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Icons,
  KPI, KPIStrip,
  Drawer, DrawerBody, DrawerSection, DrawerFooter, KVList,
  EmptyState, Button,
} from '../../shared/ui';
import api from '../../utils/api';
import AddMeterReadingDrawer from './AddMeterReadingDrawer';

const intlLocale = (lang) => (lang === 'en' ? 'en-US' : 'vi-VN');

const VND = (n, lang = 'vi') =>
  new Intl.NumberFormat(intlLocale(lang), { style: 'currency', currency: 'VND' }).format(n ?? 0);

const nowYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ── status meta ───────────────────────────────────────────────────────────────
function useStatusMeta() {
  const { t } = useTranslation('finance');
  return {
    verified: { label: t('meter.status.verified'), cls: 'text-green-700 bg-green-50  ring-green-200' },
    pending:  { label: t('meter.status.pending'),  cls: 'text-teal-700  bg-teal-50   ring-teal-200'  },
    rejected: { label: t('meter.status.rejected'), cls: 'text-red-700   bg-red-50    ring-red-200'   },
  };
}

function StatusPill({ status }) {
  const meta = useStatusMeta();
  const m = meta[status] ?? { label: status, cls: 'text-stone-600 bg-stone-100 ring-stone-200' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${m.cls}`}>
      {m.label}
    </span>
  );
}

function TypeChip({ type }) {
  const { t } = useTranslation('finance');
  if (type === 'electricity' || type === 'electric') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
        <Icons.Zap size={12} />
        {t('meter.type.electricity')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700">
      <Icons.Droplets size={12} />
      {t('meter.type.water')}
    </span>
  );
}

// ── image url helper ──────────────────────────────────────────────────────────
const getImageUrl = (r) => {
  if (r?.image_url && !r.image_url.startsWith('data:')) return r.image_url;
  if (r?.image_file_id) return `/api/meter-readings/image/${r.id}`;
  if (r?.image_url) return r.image_url;
  return null;
};

// ── verify / edit drawer ──────────────────────────────────────────────────────
function VerifyDrawer({ reading, onClose, onSuccess }) {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation(['finance', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const locale = intlLocale(lang);
  const [adjustedReading, setAdjustedReading] = useState(
    reading ? String(reading.adjusted_reading ?? reading.reading_value ?? '') : ''
  );
  const [adminNote, setAdminNote] = useState(reading?.admin_note ?? '');
  const [isBaseReading, setIsBaseReading] = useState(reading?.is_base_reading ?? false);
  const [readingMonth, setReadingMonth] = useState(reading?.month ?? '');
  const [imgError, setImgError] = useState(false);
  const [editingVerified, setEditingVerified] = useState(false);

  const verify = useMutation({
    mutationFn: ({ status }) =>
      api.put(`/api/admin/meter-readings/${reading.id}/verify`, {
        status,
        admin_note: adminNote,
        adjusted_reading: adjustedReading !== '' ? Number(adjustedReading) : undefined,
        is_base_reading: isBaseReading,
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      onSuccess?.();
      onClose();
    },
  });

  const adjust = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/meter-readings/${reading.id}`, {
        admin_note: adminNote || undefined,
        adjusted_reading: adjustedReading !== '' ? Number(adjustedReading) : undefined,
        is_base_reading: isBaseReading,
        month: readingMonth || undefined,
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      setEditingVerified(false);
      // Keep drawer open — derived drawerReading auto-updates from fresh query data
    },
    onError: (err) => toast.error(err?.response?.data?.detail || 'Điều chỉnh thất bại'),
  });

  if (!reading) return null;

  const imageUrl = getImageUrl(reading);
  const usage = reading.usage_amount;
  const cost  = reading.total_cost;

  const fieldCls = 'w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400';

  return (
    <>
      <DrawerBody>
        {/* Status */}
        <DrawerSection>
          <div className="flex items-center gap-3">
            <StatusPill status={reading.status} />
            <TypeChip type={reading.reading_type} />
            {reading.is_base_reading && (
              <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-full">
                {t('finance:meter.baseReading')}
              </span>
            )}
          </div>
        </DrawerSection>

        {/* Info */}
        <DrawerSection title={t('finance:meter.drawer.section.info')}>
          <KVList items={[
            [t('finance:meter.drawer.kv.room'),
              reading.floor
                ? t('finance:meter.drawer.kv.roomFloor', { room: reading.room_number || '—', floor: reading.floor })
                : (reading.room_number || '—')],
            [t('finance:meter.drawer.kv.building'), reading.building_name || '—'],
            [t('finance:meter.drawer.kv.renter'),   reading.renter_name || '—'],
            [t('finance:meter.drawer.kv.billingMonth', 'Kỳ tính tiền'), reading.month || '—'],
            [t('finance:meter.drawer.kv.readingDate', 'Ngày ghi'),
              reading.reading_date
                ? new Date(reading.reading_date).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
                : '—'],
            [t('finance:meter.drawer.kv.loggedAt', 'Ngày nhập'),
              reading.created_at
                ? new Date(reading.created_at).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '—'],
            ...(reading.verified_at ? [[
              t('finance:meter.drawer.kv.verifiedAt', 'Ngày xác nhận'),
              new Date(reading.verified_at).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }),
            ]] : []),
          ]} />
        </DrawerSection>

        {/* Readings */}
        <DrawerSection title={t('finance:meter.drawer.section.readings')}>
          {/* Show adjusted_reading as the effective value when admin has overridden it */}
          {reading.adjusted_reading != null && reading.adjusted_reading !== reading.reading_value && (
            <p className="text-[11px] text-amber-600 bg-amber-50 ring-1 ring-amber-200 px-2 py-1 rounded mb-2">
              {t('finance:meter.drawer.adjustedNote', 'Đã điều chỉnh từ')} {reading.reading_value} → <strong>{reading.adjusted_reading}</strong>
            </p>
          )}
          <KVList items={[
            [t('finance:meter.drawer.kv.previousReading'),
              reading.previous_reading != null
                ? reading.previous_reading.toLocaleString(locale)
                : t('finance:meter.drawer.kv.previousEmpty')],
            [t('finance:meter.drawer.kv.newReading'),
              (reading.adjusted_reading ?? reading.reading_value ?? 0).toLocaleString(locale)],
            ...(usage != null ? [[
              t('finance:meter.drawer.kv.usage'),
              `${usage.toLocaleString(locale)} ${reading.reading_type === 'water' ? 'm³' : 'kWh'}`,
            ]] : []),
            ...(cost != null  ? [[t('finance:meter.drawer.kv.cost'), VND(cost, lang)]] : []),
          ]} />
        </DrawerSection>

        {/* Proof image */}
        {imageUrl && !imgError && (
          <DrawerSection title={t('finance:meter.drawer.section.image')}>
            <a href={imageUrl} target="_blank" rel="noreferrer" className="block">
              <img
                src={imageUrl}
                alt={t('finance:meter.drawer.imageAlt')}
                onError={() => setImgError(true)}
                className="w-full rounded-lg border border-stone-200 object-cover max-h-52 hover:opacity-90 transition-opacity"
              />
            </a>
          </DrawerSection>
        )}

        {/* Adjust / verify form — always available to admin */}
        {(reading.status === 'pending' || editingVerified) && (
          <DrawerSection title={
            reading.status === 'pending'
              ? t('finance:meter.drawer.section.verify')
              : t('finance:meter.drawer.section.adjust', 'Điều chỉnh chỉ số')
          }>
            <div className="space-y-3">
              {editingVerified && (
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">
                    {t('finance:meter.drawer.readingMonth', 'Tháng ghi chỉ số')}
                    <span className="ml-1 text-[10px] font-normal text-stone-400">
                      ({t('finance:meter.drawer.readingMonthHint', 'tính tiền vào tháng kế tiếp')})
                    </span>
                  </label>
                  <input
                    type="month"
                    value={readingMonth}
                    onChange={e => setReadingMonth(e.target.value)}
                    className={fieldCls}
                  />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:meter.drawer.adjustedReading')}
                  <span className="ml-1 text-[10px] font-normal text-stone-400">
                    ({t('finance:meter.drawer.originalReading', 'gốc')}: {reading.reading_value})
                  </span>
                </label>
                <input
                  type="number"
                  value={adjustedReading}
                  onChange={e => setAdjustedReading(e.target.value)}
                  placeholder={String(reading.reading_value ?? '')}
                  className={fieldCls}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-stone-600 mb-1.5">
                  {t('finance:meter.drawer.note')}
                </label>
                <textarea
                  value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                  rows={2}
                  placeholder={t('finance:meter.drawer.notePlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                />
              </div>

              {/* Base reading toggle — available for both pending verify and post-verify adjust */}
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isBaseReading}
                  onChange={e => setIsBaseReading(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500"
                />
                <div>
                  <p className="text-[13px] font-medium text-stone-700 group-hover:text-stone-900">
                    {t('finance:meter.baseReading')}
                  </p>
                  <p className="text-[11px] text-stone-400 mt-0.5">
                    {t('finance:meter.baseReadingHint', 'Đánh dấu là chỉ số đầu kỳ — không tính tiêu thụ, dùng làm gốc cho kỳ sau')}
                  </p>
                </div>
              </label>
            </div>
          </DrawerSection>
        )}

        {/* Admin note (readonly, when not in edit mode) */}
        {reading.status !== 'pending' && !editingVerified && reading.admin_note && (
          <DrawerSection title={t('finance:meter.drawer.section.adminNote')}>
            <p className="text-[13px] text-stone-700">{reading.admin_note}</p>
          </DrawerSection>
        )}
      </DrawerBody>

      {/* Footer — pending: verify/reject; verified/rejected: adjust button */}
      {reading.status === 'pending' ? (
        <DrawerFooter>
          <button
            onClick={() => verify.mutate({ status: 'rejected' })}
            disabled={verify.isPending}
            className="flex-1 h-9 rounded-lg border border-red-200 text-red-600 text-[13px] font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            {t('finance:meter.action.reject')}
          </button>
          <button
            onClick={() => verify.mutate({ status: 'verified' })}
            disabled={verify.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {verify.isPending ? t('finance:meter.action.processing') : t('finance:meter.action.confirm')}
          </button>
        </DrawerFooter>
      ) : editingVerified ? (
        <DrawerFooter>
          <button
            onClick={() => { setEditingVerified(false); setAdjustedReading(String(reading.adjusted_reading ?? reading.reading_value ?? '')); setAdminNote(reading.admin_note ?? ''); setIsBaseReading(reading.is_base_reading ?? false); setReadingMonth(reading.month ?? ''); }}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors"
          >
            {t('common:action.cancel')}
          </button>
          <button
            onClick={() => adjust.mutate()}
            disabled={adjust.isPending}
            className="flex-1 h-9 rounded-lg bg-teal-600 text-white text-[13px] font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {adjust.isPending ? t('finance:meter.action.processing') : t('finance:meter.action.saveAdjust', 'Lưu điều chỉnh')}
          </button>
        </DrawerFooter>
      ) : (
        <DrawerFooter>
          <button
            onClick={onClose}
            className="flex-1 h-9 rounded-lg border border-stone-200 text-stone-700 text-[13px] font-medium hover:bg-stone-50 transition-colors"
          >
            {t('common:action.close')}
          </button>
          <button
            onClick={() => setEditingVerified(true)}
            className="flex items-center gap-1.5 px-4 h-9 rounded-lg border border-teal-300 text-teal-700 text-[13px] font-medium hover:bg-teal-50 transition-colors"
          >
            <Icons.Edit size={13} />
            {t('finance:meter.action.adjust', 'Điều chỉnh')}
          </button>
        </DrawerFooter>
      )}
    </>
  );
}

// ── Recalculate series button ──────────────────────────────────────────────────
function RecalcButton() {
  const { t } = useTranslation(['finance', 'common']);
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);

  const recalc = useMutation({
    mutationFn: () => api.post('/api/admin/meter-readings/recalculate-series').then(r => r.data),
    onSuccess: (data) => {
      toast.success(t('finance:meter.recalc.success', { count: data.updated ?? 0 }));
      qc.invalidateQueries({ queryKey: ['meter-readings'] });
      setConfirm(false);
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || t('finance:meter.recalc.error'));
      setConfirm(false);
    },
  });

  if (confirm) {
    return (
      <div className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-amber-300 bg-amber-50 text-[13px] font-medium text-amber-800">
        <Icons.Alert size={14} className="shrink-0" />
        <span>{t('finance:meter.recalc.confirm')}</span>
        <button
          onClick={() => recalc.mutate()}
          disabled={recalc.isPending}
          className="ml-1 px-2 py-0.5 rounded bg-amber-600 text-white text-[12px] font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors"
        >
          {recalc.isPending ? t('common:action.processing') : t('common:action.confirm')}
        </button>
        <button
          onClick={() => setConfirm(false)}
          className="ml-0.5 text-amber-600 hover:text-amber-800"
        >
          <Icons.X size={13} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setConfirm(true)}
      title={t('finance:meter.recalc.tooltip')}
      className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
    >
      <Icons.Refresh size={14} />
      {t('finance:meter.recalc.button')}
    </button>
  );
}

// ── main component ─────────────────────────────────────────────────────────────
function useTabs() {
  const { t } = useTranslation('finance');
  return [
    { key: 'all',      label: t('meter.tab.all') },
    { key: 'pending',  label: t('meter.tab.pending') },
    { key: 'verified', label: t('meter.tab.verified') },
    { key: 'rejected', label: t('meter.tab.rejected') },
  ];
}

export default function MeterReadingsManagement() {
  const qc = useQueryClient();
  const { t, i18n } = useTranslation(['finance', 'common']);
  const lang = i18n.language?.startsWith('en') ? 'en' : 'vi';
  const TABS = useTabs();
  // Empty string = "All months". User can set a specific month via the filter.
  const [month, setMonth] = useState('');
  const [tab, setTab] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [buildingId, setBuildingId] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [drawerReadingId, setDrawerReadingId] = useState(null);

  // ── data ──────────────────────────────────────────────────────────────────────
  const { data: rawReadings = [], isLoading } = useQuery({
    queryKey: ['meter-readings', month],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '500' });
      if (month) params.set('month', month);
      return api.get(`/api/admin/meter-readings/history?${params}`)
        .then(r => {
          const d = r.data;
          return Array.isArray(d) ? d : (d?.readings ?? []);
        })
        .catch(() => {
          const fallbackParams = month ? { month, limit: 500 } : { limit: 500 };
          return api.get('/api/admin/meter-readings', { params: fallbackParams })
            .then(r => r.data ?? []);
        });
    },
    staleTime: 2 * 60 * 1000,
  });

  const { data: buildings = [] } = useQuery({
    queryKey: ['buildings-filter'],
    queryFn: () =>
      api.get('/api/properties/buildings').then(r => {
        const d = r.data;
        return Array.isArray(d) ? d : (d?.buildings ?? []);
      }),
    staleTime: 10 * 60 * 1000,
  });

  // ── computed ──────────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const nonBase = rawReadings.filter(r => !r.is_base_reading);
    const pending  = nonBase.filter(r => r.status === 'pending').length;
    const verified = nonBase.filter(r => r.status === 'verified').length;
    const totalCost = nonBase
      .filter(r => r.status === 'verified')
      .reduce((s, r) => s + (parseFloat(r.total_cost) || 0), 0);
    return { total: nonBase.length, pending, verified, totalCost };
  }, [rawReadings]);

  const tabCounts = useMemo(() => ({
    all:      rawReadings.filter(r => !r.is_base_reading).length,
    pending:  rawReadings.filter(r => !r.is_base_reading && r.status === 'pending').length,
    verified: rawReadings.filter(r => !r.is_base_reading && r.status === 'verified').length,
    rejected: rawReadings.filter(r => !r.is_base_reading && r.status === 'rejected').length,
  }), [rawReadings]);

  const rows = useMemo(() => {
    let list = rawReadings.filter(r => {
      if (tab !== 'all' && r.status !== tab) return false;
      if (typeFilter !== 'all') {
        const ty = r.reading_type;
        if (typeFilter === 'electricity' && ty !== 'electricity' && ty !== 'electric') return false;
        if (typeFilter === 'water' && ty !== 'water') return false;
      }
      if (buildingId && String(r.building_id) !== String(buildingId)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.room_number?.toLowerCase().includes(q) &&
          !r.renter_name?.toLowerCase().includes(q) &&
          !r.building_name?.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
    return list;
  }, [rawReadings, tab, typeFilter, buildingId, search]);

  // Derive live reading from query data — never stale after mutation + invalidation
  const drawerReading = useMemo(
    () => drawerReadingId != null ? (rawReadings.find(r => r.id === drawerReadingId) ?? null) : null,
    [rawReadings, drawerReadingId]
  );

  const locale = intlLocale(lang);
  const drawerTitle = drawerReading
    ? (drawerReading.reading_type === 'water'
        ? t('finance:meter.drawer.titleWater', { room: drawerReading.room_number || '' })
        : t('finance:meter.drawer.titleElectricity', { room: drawerReading.room_number || '' }))
    : '';

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">{t('finance:meter.title')}</h1>
          <p className="text-sm text-stone-500 mt-0.5">{t('finance:meter.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['meter-readings'] })}
            className="flex items-center gap-1.5 px-3 h-9 rounded-lg border border-stone-200 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition-colors"
          >
            <Icons.Refresh size={14} />
            {t('common:action.refresh')}
          </button>
          <RecalcButton />
          <Button
            kind="primary"
            size="md"
            iconLeft={<Icons.Plus size={14} />}
            onClick={() => setShowAdd(true)}
          >
            {t('finance:meter.add.button')}
          </Button>
        </div>
      </div>

      {/* ── KPI strip ── */}
      <KPIStrip>
        <KPI
          label={t('finance:meter.kpi.totalReadings')}
          value={String(kpi.total)}
          sub={t('finance:meter.kpi.thisMonth')}
          icon={<Icons.Zap />}
          accent="primary"
        />
        <KPI
          label={t('finance:meter.kpi.pending')}
          value={String(kpi.pending)}
          sub={t('finance:meter.kpi.pendingSub')}
          icon={<Icons.Clock />}
          accent={kpi.pending > 0 ? 'warning' : 'neutral'}
        />
        <KPI
          label={t('finance:meter.kpi.verified')}
          value={String(kpi.verified)}
          icon={<Icons.CheckCircle />}
          accent="success"
        />
        <KPI
          label={t('finance:meter.kpi.totalCost')}
          value={VND(kpi.totalCost, lang)}
          sub={t('finance:meter.kpi.totalCostSub')}
          icon={<Icons.Receipt />}
          accent="info"
        />
      </KPIStrip>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1 border-b border-stone-200 overflow-x-auto">
        {TABS.map(tb => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${
              tab === tb.key
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-stone-500 hover:text-stone-700'
            }`}
          >
            {tb.label}
            {tabCounts[tb.key] > 0 && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                tab === tb.key ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-500'
              }`}>
                {tabCounts[tb.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-stretch h-9 rounded-lg border border-stone-200 bg-white overflow-hidden">
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            placeholder={t('finance:meter.filter.allMonths')}
            className="h-full px-3 text-[13px] text-stone-700 bg-transparent focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
          {month ? (
            <button
              type="button"
              onClick={() => setMonth('')}
              title={t('finance:meter.filter.allMonths')}
              className="px-2 border-l border-stone-200 text-stone-400 hover:text-stone-700 hover:bg-stone-50 transition-colors"
            >
              <Icons.X size={14} />
            </button>
          ) : (
            <span className="px-3 border-l border-stone-200 inline-flex items-center text-[11px] font-medium text-teal-700 bg-teal-50">
              {t('finance:meter.filter.allMonths')}
            </span>
          )}
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
        >
          <option value="all">{t('finance:meter.type.allTypes')}</option>
          <option value="electricity">{t('finance:meter.type.electricity')}</option>
          <option value="water">{t('finance:meter.type.water')}</option>
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Icons.Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('finance:payment.filter.searchPlaceholder')}
            className="w-full h-9 pl-8 pr-3 rounded-lg border border-stone-200 text-[13px] placeholder:text-stone-400 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          />
        </div>
        {buildings.length > 0 && (
          <select
            value={buildingId}
            onChange={e => setBuildingId(e.target.value)}
            className="h-9 px-3 rounded-lg border border-stone-200 text-[13px] text-stone-700 bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
          >
            <option value="">{t('finance:payment.filter.buildingAll')}</option>
            {buildings.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        )}
        <span className="ml-auto text-xs text-stone-400">
          {t('finance:payment.filter.resultCount', { count: rows.length })}
        </span>
      </div>

      {/* ── Dense table ── */}
      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-stone-200 bg-stone-50">
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:meter.col.type')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:meter.col.room')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden md:table-cell">
                {t('finance:meter.col.renter')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">
                {t('finance:meter.col.reading')}
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden sm:table-cell">
                {t('finance:meter.col.usage')}
              </th>
              <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:meter.col.amount')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden lg:table-cell">
                {t('finance:meter.col.readingDate', 'Ngày ghi')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500 hidden lg:table-cell">
                {t('finance:meter.col.billingMonth', 'Kỳ tính tiền')}
              </th>
              <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                {t('finance:meter.col.status')}
              </th>
              <th className="w-10 px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="h-10">
                  {Array.from({ length: 7 }).map((__, j) => (
                    <td key={j} className="px-3">
                      <div className="h-3.5 bg-stone-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState
                    compact
                    icon={<Icons.Zap />}
                    title={t('finance:meter.empty.title')}
                    description={t('finance:meter.empty.description')}
                  />
                </td>
              </tr>
            ) : (
              rows.map(r => (
                <tr
                  key={r.id}
                  onClick={() => setDrawerReadingId(r.id)}
                  className={`h-10 cursor-pointer transition-colors hover:bg-stone-50 ${
                    r.is_base_reading ? 'bg-amber-50/40' : ''
                  }`}
                >
                  <td className="px-3">
                    <TypeChip type={r.reading_type} />
                  </td>
                  <td className="px-3 font-medium text-stone-800">
                    {r.room_number || '—'}
                    {r.is_base_reading && (
                      <span className="ml-1.5 text-[10px] text-amber-600 font-semibold">
                        {t('finance:meter.baseReadingShort')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 text-stone-500 hidden md:table-cell truncate max-w-[140px]">
                    {r.renter_name || '—'}
                  </td>
                  <td className="px-3 text-stone-600 hidden sm:table-cell tabular-nums">
                    {(() => {
                      const effective = r.adjusted_reading ?? r.reading_value ?? 0;
                      const adjusted  = r.adjusted_reading != null && r.adjusted_reading !== r.reading_value;
                      const display   = <span className={adjusted ? 'text-amber-700 font-semibold' : ''}>{effective.toLocaleString(locale)}</span>;
                      return r.previous_reading != null
                        ? <>{r.previous_reading.toLocaleString(locale)} → {display}</>
                        : display;
                    })()}
                  </td>
                  <td className="px-3 text-right font-medium text-teal-700 hidden sm:table-cell tabular-nums">
                    {r.usage_amount != null
                      ? `${r.usage_amount.toLocaleString(locale)} ${r.reading_type === 'water' ? 'm³' : 'kWh'}`
                      : '—'
                    }
                  </td>
                  <td className="px-3 text-right font-medium text-stone-900 tabular-nums">
                    {r.total_cost != null ? VND(r.total_cost, lang) : '—'}
                  </td>
                  {/* Logged date — when the reading was physically taken */}
                  <td className="px-3 text-stone-500 text-[12px] hidden lg:table-cell tabular-nums">
                    {r.reading_date
                      ? new Date(r.reading_date).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
                      : r.created_at
                        ? new Date(r.created_at).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '—'}
                  </td>
                  {/* Billing month */}
                  <td className="px-3 text-stone-500 text-[12px] hidden lg:table-cell">
                    {r.month || '—'}
                  </td>
                  <td className="px-3">
                    <StatusPill status={r.status} />
                  </td>
                  <td className="px-3" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setDrawerReadingId(r.id)}
                      className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100 transition-colors"
                      title={t('finance:meter.action.detail')}
                    >
                      <Icons.Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Detail / verify drawer ── */}
      <Drawer
        open={!!drawerReading}
        onClose={() => setDrawerReadingId(null)}
        title={drawerTitle}
        subtitle={drawerReading ? `${drawerReading.building_name || ''} · ${drawerReading.month || ''}` : ''}
        width={400}
      >
        {drawerReading && (
          <VerifyDrawer
            reading={drawerReading}
            onClose={() => setDrawerReadingId(null)}
            onSuccess={() => {/* drawer stays open to show updated values */}}
          />
        )}
      </Drawer>

      {/* ── Add reading drawer (Manual / OCR / Bulk) ── */}
      <AddMeterReadingDrawer open={showAdd} onClose={() => setShowAdd(false)} />
    </div>
  );
}
